import { useEffect, useState, useCallback } from "react";
import { Loader2, ChevronDown, ChevronUp, RotateCcw, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { inp, L, Modal, money } from "./inputs";

const FULFILL = [["awaiting_fulfillment", "Awaiting Fulfillment"], ["sent_to_printer", "Sent to Printer (POD)"], ["shipped", "Shipped"], ["delivered", "Delivered"], ["n/a", "N/A (tickets only)"]];

export const OrdersTab = ({ canRefund }) => {
  const [orders, setOrders] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [openId, setOpenId] = useState(null);
  const [refunding, setRefunding] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/orders", { params: { q: q || undefined, status: status || undefined, date_from: from || undefined, date_to: to || undefined } });
      setOrders(data);
    } catch { toast.error("Failed to load orders"); }
  }, [q, status, from, to]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const doRefund = async () => {
    try {
      await api.post(`/admin/orders/${refunding.id}/refund`, { amount: Number(refunding.amount), reason: refunding.reason || "" });
      toast.success("Refund recorded — remember to process the actual refund in the Stripe/PayPal dashboard");
      setRefunding(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
  };
  const setFulfillment = async (o, val) => {
    try { await api.put(`/admin/orders/${o.id}/fulfillment`, { fulfillment_status: val, tracking: o.tracking || "" }); toast.success("Updated"); load(); }
    catch { toast.error("Update failed"); }
  };
  const saveTracking = async (o, tracking) => {
    try { await api.put(`/admin/orders/${o.id}/fulfillment`, { fulfillment_status: o.fulfillment_status, tracking }); toast.success("Tracking saved"); load(); }
    catch { toast.error("Update failed"); }
  };

  const badge = (o) => {
    if (o.refund_status === "full") return <span className="text-xs px-2.5 py-1 rounded-full bg-[#E63946]/15 text-[#E63946]">Refunded</span>;
    if (o.refund_status === "partial") return <span className="text-xs px-2.5 py-1 rounded-full bg-yellow-500/15 text-yellow-400">Partial Refund</span>;
    if (o.payment_status === "paid") return <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400">Paid</span>;
    return <span className="text-xs px-2.5 py-1 rounded-full bg-white/10 text-white/50">{o.payment_status}</span>;
  };

  return (
    <div data-testid="orders-tab">
      <h1 className="font-display font-bold text-2xl mb-6">Orders</h1>
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input className={`${inp} pl-8`} placeholder="Search name, email, phone, order #" data-testid="orders-search-input" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <select className={`${inp} w-auto`} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="orders-status-filter">
          <option value="">All statuses</option><option value="paid">Paid</option><option value="pending">Pending</option>
        </select>
        <input type="date" className={`${inp} w-auto`} value={from} onChange={(e) => setFrom(e.target.value)} />
        <input type="date" className={`${inp} w-auto`} value={to} onChange={(e) => setTo(e.target.value)} />
      </div>
      {!orders ? <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#E63946]" /></div> : (
        <div className="space-y-2">
          {orders.map((o) => {
            const refunded = (o.refunds || []).reduce((s, r) => s + Number(r.amount || 0), 0);
            const isOpen = openId === o.id;
            return (
              <div key={o.id} data-testid="order-row" className="bg-white/[0.03] border border-white/10 rounded-lg">
                <button onClick={() => setOpenId(isOpen ? null : o.id)} className="w-full flex items-center gap-4 p-4 text-left">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate font-mono text-sm">{o.order_number} <span className="font-sans text-white/50 font-normal">· {o.name}</span></p>
                    <p className="text-sm text-white/40 truncate">{o.email} · {new Date(o.created_at).toLocaleString()} · {o.payment_method}</p>
                  </div>
                  <span className="font-display font-bold">{money(o.total)}</span>
                  {badge(o)}
                  {isOpen ? <ChevronUp size={16} className="text-white/40" /> : <ChevronDown size={16} className="text-white/40" />}
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 border-t border-white/10 pt-4 grid md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-white/40 mb-2">Items</p>
                      {(o.items || []).map((i, idx) => (
                        <div key={`${i.name}-${idx}`} className="flex justify-between text-sm py-1"><span>{i.name} × {i.qty}</span><span>{money(i.unit * i.qty)}</span></div>
                      ))}
                      <div className="border-t border-white/10 mt-2 pt-2 space-y-1 text-sm">
                        <div className="flex justify-between text-white/60"><span>Subtotal</span><span>{money(o.subtotal)}</span></div>
                        {Number(o.discount) > 0 && <div className="flex justify-between text-[#E63946]"><span>Discount {o.promo_code ? `(${o.promo_code})` : ""}</span><span>-{money(o.discount)}</span></div>}
                        {Number(o.service_fee) > 0 && <div className="flex justify-between text-white/60"><span>{o.service_fee_label || "Service Fee"}</span><span>{money(o.service_fee)}</span></div>}
                        {Number(o.shipping) > 0 && <div className="flex justify-between text-white/60"><span>Shipping</span><span>{money(o.shipping)}</span></div>}
                        {Number(o.tax) > 0 && <div className="flex justify-between text-white/60"><span>Sales Tax</span><span>{money(o.tax)}</span></div>}
                        <div className="flex justify-between font-bold"><span>Total</span><span>{money(o.total)}</span></div>
                        {refunded > 0 && <div className="flex justify-between text-[#E63946]"><span>Refunded</span><span>-{money(refunded)}</span></div>}
                      </div>
                      {o.phone && <p className="text-sm text-white/40 mt-3">Phone: {o.phone}</p>}
                      {o.ticket_code && <p className="text-sm text-white/40 mt-1">Ticket code: <span className="font-mono">{o.ticket_code}</span> {o.checked_in ? "· checked in" : ""}</p>}
                    </div>
                    <div className="space-y-4">
                      {(o.items || []).some((i) => i.type === "product") && (
                        <div>
                          <p className="text-xs uppercase tracking-wider text-white/40 mb-2">Fulfillment</p>
                          <select className={inp} value={o.fulfillment_status || "awaiting_fulfillment"} onChange={(e) => setFulfillment(o, e.target.value)} data-testid="fulfillment-select">
                            {FULFILL.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                          <div className="flex gap-2 mt-2">
                            <input className={inp} placeholder="Tracking number / link" defaultValue={o.tracking || ""} id={`trk-${o.id}`} />
                            <button onClick={() => saveTracking(o, document.getElementById(`trk-${o.id}`).value)} className="rounded-full border border-white/15 px-4 text-sm hover:bg-white/5 shrink-0">Save</button>
                          </div>
                        </div>
                      )}
                      {(o.refunds || []).length > 0 && (
                        <div>
                          <p className="text-xs uppercase tracking-wider text-white/40 mb-2">Refund History</p>
                          {o.refunds.map((r) => (
                            <p key={r.id} className="text-sm text-white/50">-{money(r.amount)} · {r.reason || "no reason"} · by {r.by} · {new Date(r.at).toLocaleDateString()}</p>
                          ))}
                        </div>
                      )}
                      {canRefund && o.payment_status === "paid" && refunded < Number(o.total) && (
                        <button onClick={() => setRefunding({ id: o.id, order_number: o.order_number, max: Number(o.total) - refunded, amount: (Number(o.total) - refunded).toFixed(2), reason: "" })}
                          data-testid="refund-btn" className="inline-flex items-center gap-2 rounded-full border border-[#E63946]/50 text-[#E63946] px-5 py-2.5 text-sm hover:bg-[#E63946]/10"><RotateCcw size={14} /> Record Refund</button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {orders.length === 0 && <p className="text-white/40 py-16 text-center">No orders found.</p>}
        </div>
      )}

      {refunding && (
        <Modal title={`Refund ${refunding.order_number}`} onClose={() => setRefunding(null)} onSave={doRefund} saveLabel="Record Refund">
          <p className="text-sm text-yellow-400/90 bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">This records the refund here for your books. You still need to click the actual refund in your Stripe or PayPal dashboard — money is not moved automatically.</p>
          <div><L>Refund Amount (max {money(refunding.max)})</L><input type="number" step="0.01" className={inp} data-testid="refund-amount-input" value={refunding.amount} onChange={(e) => setRefunding({ ...refunding, amount: e.target.value })} /></div>
          <div><L>Reason</L><input className={inp} value={refunding.reason} onChange={(e) => setRefunding({ ...refunding, reason: e.target.value })} /></div>
          <p className="text-sm text-white/50">You are about to record a refund of <span className="font-bold text-white">{money(refunding.amount)}</span>. Financial records are never deleted.</p>
        </Modal>
      )}
    </div>
  );
};
